import { ApiError, StaleError } from '@/api/client';
import {
  type ApiUserFull,
  type UserWrite,
  useAbsences,
  useRoles,
  useShifts,
  useTeams,
  useUser,
  useUserWrites,
  useUsers,
} from '@/api/data';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
/**
 * The employee page (owner brief, 2026-09-21): header, four figures and seven
 * tabs (Tilgjengelighet since issue #70), the address naming the tab (/ansatte/14/fravaer). Reads GET
 * /admin/users/:id; edits go through the Rediger ansatt drawer, one PUT on Lagre
 * (ansatt/EditDrawer.tsx). Company access keeps its own save (Bedriftstilgang).
 */
import { T } from '@/i18n';
import { breakPolicy, oslo, today } from '@/rules';
import { modeOf } from '@/rules/breakSetting';
import { setDirty, useDirty } from '@/shell/context';
import { useDiscard } from '@/shell/leave';
import { useTenant } from '@/shell/tenant';
import { Button, Drawer, Empty, Menu, Panel, Skeleton, toast } from '@/ui';
import { DateInput, birthDay } from '@/ui/DateInput';
import { fullName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { Notice, PageHead, Status, TabPanel, Tabs } from '@/ui/page';
import { useQueryClient } from '@tanstack/react-query';
import { addDays, mondayOf } from '@timefly/core';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FerdigheterPanel } from './ansatt/FerdigheterPanel';
import './ansatt/ansatte.css';
import { Files } from './Files';
import { EditDrawer, tabOfField } from './ansatt/EditDrawer';
import { FravaerTab } from './ansatt/FravaerTab';
import { Figures, Oversikt } from './ansatt/Oversikt';
import { TidTab } from './ansatt/TidTab';
import { TilgangTab } from './ansatt/TilgangTab';
import { TilgjengelighetTab } from './ansatt/TilgjengelighetTab';
import { VakterTab } from './ansatt/VakterTab';
import { fullBody } from './ansatt/body';
import { dropDraft, loadDraft, saveDraft } from './ansatt/draft';
import { EmailTypo } from './ansatt/emailTypo';
import { InviteReview } from './ansatt/invite';
import { useAccountMail } from './ansatt/mail';
import { roleIn, takenRefusal } from './ansatt/roles';
import { neighbours, setupItems } from './ansatt/roster';
import { type Row as Person, type Task, fieldFor, loadQueue, nextInQueue, remaining } from './ansatt/tasks';
import { safeReturn } from './arbeidstid/period';

/** The queue's task as the back link names it (#270). */
const BACK_TO: Record<Task, string> = {
  all: 'Tilbake til ansatte',
  hours: 'Tilbake til avtalt arbeidstid',
  setup: 'Tilbake til ansettelsesopplysninger',
  number: 'Tilbake til mangler ansattnummer',
  uninvited: 'Tilbake til ikke invitert',
  pending: 'Tilbake til venter på innlogging',
};
const TASK_NAME: Record<Task, string> = {
  all: 'Ansatte',
  hours: 'Avtalt arbeidstid',
  setup: 'Ansettelsesopplysninger',
  number: 'Mangler ansattnummer',
  uninvited: 'Ikke invitert',
  pending: 'Venter på første innlogging',
};
import { nextShift, onDuty, weekFigures } from './ansatt/summary';

const TABS = [
  // #154 BR-12: each tab's outline icon beside its label
  { key: '', label: 'Oversikt', icon: 'user' },
  { key: 'vakter', label: 'Vakter', icon: 'calendar' },
  { key: 'tid', label: 'Tid & lønn', icon: 'clock' },
  { key: 'fravaer', label: 'Fravær', icon: 'calendarOff' },
  { key: 'tilgjengelighet', label: 'Tilgjengelighet', icon: 'check' },
  { key: 'dokumenter', label: 'Dokumenter', icon: 'document' },
  { key: 'tilgang', label: 'Tilgang', icon: 'lock' },
] as const;
type Tab = (typeof TABS)[number]['key'];

/**
 * The new-employee form as it opens: nothing typed yet, so leaving it asks nothing (#272 B-10). No
 * welcome mail: creating a person, inviting them and giving access are separate actions (#270).
 */
const BLANK: UserWrite = { contract_type: 'hourly', active_status: true };
/** The fields a create needs, by the label the form shows them under (#272 B-09). */
const REQUIRED_LABEL: Record<string, string> = {
  name: 'Fornavn',
  lastname: 'Etternavn',
  birth_date: 'Fødselsdato',
  username: 'Brukernavn',
};
/** What the create form keeps as a draft (#270): the fields, the week choice and the team. */
interface NewDraft {
  nw: UserWrite;
  week: { kind: 'heltid' | 'deltid' | null; hours: string };
  team: string;
}

function Row({
  label,
  children,
  note,
}: { label: string; children: React.ReactNode; note?: string | undefined }) {
  return (
    <div className="prow">
      <b>{label}</b>
      <span className="pv">
        {children}
        {note && <em>{note}</em>}
      </span>
    </div>
  );
}

export default function Ansatt() {
  const { id, fane } = useParams();
  const isNew = id === 'ny';
  const uid = isNew ? null : Number(id);
  const can = useCan();
  const nav = useNavigate();
  // added from the first-run setup: back there once the person is made (owner, 2026-09-27)
  const [sp, setSp] = useSearchParams();
  const fromSetup = sp.get('fra') === 'oppsett';
  // "su rodyklėm eiti pirmyn/atgal per kitus darbuotojus": filling in details for a lot of
  // people meant walking back to the list every time (product user, 2026-09-21). The list is
  // already in the cache, so this costs no request.
  const rosterQ = useUsers();
  const { tenant } = useTenant();
  // the company's week a new full-timer follows; 37,5 where the company has set none (plan 0027 B)
  const companyWeek = String(
    tenant?.agreed_week_hours != null ? Number(tenant.agreed_week_hours) : 37.5,
  ).replace('.', ',');
  const userQ = useUser(uid);
  const teamsQ = useTeams();
  const qc = useQueryClient();
  const loc = useLocation();
  const meId = useAuth((s) => s.user?.id);
  // the list this person was opened from (#270 EMP-02, EMP-07): Tilbake, the arrows and «Lagre og neste» walk it
  const queue = loadQueue(tenant?.id, meId);
  const accessKnown = tenant?.me?.is_admin === true;
  // what the last save left: how many remain in the task and who is next, shown until the person changes
  const [saved, setSaved] = useState<{ id: number; left: number; next: Person | null } | null>(null);
  const [inviting, setInviting] = useState(false);
  // a draft dropped from the profile's notice: drawn again without it
  const [, redraw] = useState(0);
  const w = useUserWrites();
  const mail = useAccountMail();
  const monday = mondayOf(today());
  // four weeks back for the registrations, five ahead for the next shift
  const shiftsQ = useShifts(addDays(monday, -28), addDays(monday, 34));
  const absQ = useAbsences();
  // the company's own roles, so the card names the role the company gave it; above the early
  // returns because a hook runs on every render or on none
  const rolesQ = useRoles();
  // the team a new person starts in: the list's team when opened from one (?team=), shown and changeable (#270 EMP-03)
  const teamFromScope = /^\d+$/.test(sp.get('team') ?? '') ? (sp.get('team') as string) : '';
  const [newDraft] = useState(() => (isNew ? loadDraft<NewDraft>(tenant?.id, meId, 'ny') : null));
  const [nw, setNw] = useState<UserWrite>(newDraft?.form.nw ?? BLANK);
  const [team, setTeam] = useState<string>(newDraft?.form.team ?? teamFromScope);
  // a create whose answer never arrived and could not be confirmed either way (#270 EMP-04)
  const [unsure, setUnsure] = useState<string | null>(null);
  // the required fields a create was refused for: each says so beside itself (#272 B-09)
  const [bad, setBad] = useState<string[]>([]);
  // plan 0027 B: a new person is full time (follows the company's week) or part time with their
  // own week; asked as a choice, so an empty field never passes for full time again
  const [week, setWeek] = useState<{ kind: 'heltid' | 'deltid' | null; hours: string }>(
    newDraft?.form.week ?? { kind: null, hours: '' },
  );
  const [editing, setEditing] = useState<
    false | { tab: 'person' | 'arbeid' | 'team'; focus?: string | undefined }
  >(false);
  // ?rediger=<field>: the roster's setup tag opens the editor on that field (manager redesign,
  // 2026-10-09); the address is cleared once it has been read, so Back does not reopen it
  const askedField = sp.get('rediger');
  useEffect(() => {
    if (!askedField || !userQ.data || !can('ansatte.endre')) return;
    setEditing({ tab: tabOfField(askedField), focus: askedField });
    const n = new URLSearchParams(sp);
    n.delete('rediger');
    setSp(n, { replace: true });
  }, [askedField, userQ.data, can, sp, setSp]);
  const edit = (focus?: string) => setEditing({ tab: focus ? tabOfField(focus) : 'person', focus });
  // dirty only once something differs from the blank form: an untouched Avbryt just leaves (#272 B-10);
  // the team the scope chose is part of the blank form, so it alone asks nothing either (#270)
  const typed =
    week.kind != null ||
    team !== teamFromScope ||
    Object.entries(nw).some(([k, v]) => v !== (BLANK as Record<string, unknown>)[k] && v !== '' && v != null);
  useDirty('new-employee', isNew && typed, T('Ny ansatt'));
  useDiscard('new-employee', isNew ? () => dropDraft(tenant?.id, meId, 'ny') : null);
  // kept for the tab until it is created or discarded; back to the blank form forgets it
  useEffect(() => {
    if (!isNew) return;
    if (typed) saveDraft(tenant?.id, meId, 'ny', { nw, week, team } satisfies NewDraft);
    else dropDraft(tenant?.id, meId, 'ny');
  }, [isNew, typed, nw, week, team, tenant?.id, meId]);
  // a new page takes the keyboard to its title, not to <body> (#272 B-11); again once the company is
  // known, because until then the form may not be drawn
  // biome-ignore lint/correctness/useExhaustiveDependencies: tenant?.id re-runs it when the form appears
  useEffect(() => {
    if (!isNew) return;
    const h = document.querySelector<HTMLElement>('.ans-prof h1');
    if (!h) return;
    h.tabIndex = -1;
    h.focus();
  }, [isNew, tenant?.id]);
  /**
   * Sett nytt passord (TF-WEB-COMPLETION B). The password travels only from this
   * drawer, never from the blur edits: fullBody reads the record from the GET,
   * which carries no password, and no field() ever names it. `unknown` is set
   * when no answer arrived; the drawer then says so and offers no resend.
   */
  const [pw, setPw] = useState<{
    a: string;
    b: string;
    busy: boolean;
    msg: string | null;
    unknown: boolean;
  } | null>(null);

  const u = userQ.data;
  /**
   * PUT /admin/users/:id is a full replace: every field in the body overwrites the
   * column, an absent one writes null, and username, name, lastname, phone,
   * address, email and gender go through toLowerCase(), so they must be strings
   * (admin/userController.js updateUser). The whole record goes with every change.
   */
  const patch = async (body: UserWrite, label = T('Lagret')) => {
    if (!u) return;
    try {
      await w.update.mutateAsync({ id: u.id, body: fullBody(u, body) });
      toast(label);
    } catch (e) {
      toast(takenRefusal(e) ?? T('Kunne ikke lagre: {error}', { error: (e as Error).message }));
    }
  };

  // a deep link to /ansatte/ny without the right to add people: say so, offer no form the server would refuse (#270 EMP-06)
  if (isNew && tenant && !can('ansatte.endre'))
    return (
      <div className="ans-prof">
        <Empty
          title={T('Du kan ikke legge til ansatte')}
          action={
            <Link className="btn" to="/ansatte">
              {T('Til Ansatte')}
            </Link>
          }
        >
          {T('Be en administrator legge til personen, eller gi deg tilgang til å endre ansatte.')}
        </Empty>
      </div>
    );
  if (isNew) {
    const create = async () => {
      // the birth date too: the rules for workers under 18 need the age, and nobody is planned without it (plan 0027 batch A)
      // the address may be left empty (2026-09-29): no welcome mail and no password then, so the
      // person cannot log in until one is added through Rediger
      const noEmail = !nw.email?.trim();
      // in the form's order, so the first one named is the first one focused
      const miss = ['name', 'lastname', 'birth_date', 'username'].filter(
        (k) => !(nw as Record<string, unknown>)[k],
      );
      setBad(miss);
      if (miss.length) {
        toast(T('Mangler: {list}', { list: miss.map((k) => T(REQUIRED_LABEL[k] ?? k)).join(', ') }));
        document.getElementById(`ny-${miss[0]}`)?.focus();
        return;
      }
      // required: heltid follows the company's week, deltid needs the person's own (plan 0027 B)
      if (!week.kind) {
        toast(T('Mangler: avtalt arbeidstid, heltid eller deltid'));
        return;
      }
      const own = Number(week.hours.trim().replace(',', '.'));
      if (
        week.kind === 'deltid' &&
        !(Number.isFinite(own) && own > 0 && own <= 40 && Math.round(own * 100) === own * 100)
      ) {
        toast(T('Deltid: timer per uke, et tall over 0 og høyst 40, f.eks. 22,5'));
        return;
      }
      // aml § 14-6 (1) e (#108): a temporary job states its end; the server refuses it too
      if (nw.employment_form === 'midlertidig' && !nw.end_date) {
        toast(T('Mangler: sluttdato for midlertidig ansettelse'));
        return;
      }
      // no password and no welcome mail: access is given later, by inviting the person (#270)
      const {
        password: _typed,
        send_welcome: _mail,
        max_hours_per_week: _old,
        hourly_rate: hr,
        monthly_rate: mr,
        ...rest
      } = nw;
      // pay set when the person is made (owner, 2026-09-27): only the kind chosen, commas read as decimals
      const rate = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim().replace(',', '.') : null);
      const m = rate(mr);
      const h = rate(hr);
      const pay =
        nw.contract_type === 'monthly' ? (m ? { monthly_rate: m } : {}) : h ? { hourly_rate: h } : {};
      const body: UserWrite = {
        ...rest,
        ...pay,
        email: noEmail ? '' : (nw.email ?? '').trim(),
        agreed_week_hours: week.kind === 'deltid' ? own : null,
        // the contract (#108): the form, and the end date only with a form that has one
        employment_form: nw.employment_form || null,
        end_date: nw.employment_form === 'midlertidig' ? (nw.end_date ?? null) : null,
        // the team shown on the form, or none when «Uten team» was chosen (#270 EMP-03)
        ...(team ? { team_ids: [Number(team)] } : {}),
      };
      const done = (id: number, note: string) => {
        toast(note);
        // the form is saved: only its own registration is dropped, so the move to the new card (or
        // back to /oppsett) is not stopped by «Ny ansatt er ikke lagret» (TF-REG03, P8). A refused or
        // failed create never gets here and keeps both the draft and the guard
        setDirty('new-employee', null);
        dropDraft(tenant?.id, meId, 'ny');
        nav(fromSetup ? '/oppsett' : `/ansatte/${id}`, { state: { created: true } });
      };
      /**
       * Was it made after all? The username is unique in the database, so the person, if made, is the
       * one with this username and these names; a retry can never make a second (#270 EMP-04).
       */
      const confirmMade = async (): Promise<number | null> => {
        try {
          await qc.invalidateQueries({ queryKey: ['users'] });
          const list = (qc.getQueryData(['users']) as Person[] | undefined) ?? [];
          const un = (nw.username ?? '').trim().toLowerCase();
          const hit = list.find(
            (x) =>
              x.username === un &&
              x.name === (nw.name ?? '').trim() &&
              (x.lastname ?? '') === (nw.lastname ?? '').trim(),
          );
          return hit ? hit.id : null;
        } catch {
          return null;
        }
      };
      setUnsure(null);
      try {
        const made = await w.create.mutateAsync(body);
        done(
          made.id,
          noEmail
            ? T('Ansatt opprettet uten e-post. Ingen invitasjon er sendt.')
            : T('Ansatt opprettet. Ingen invitasjon er sendt.'),
        );
      } catch (e) {
        const err = e instanceof ApiError && !(e instanceof StaleError) ? e : null;
        // a refusal the server explained, other than a username already taken, is final: the draft stays
        if (err && err.status < 500 && err.code !== 'username_taken') {
          toast(takenRefusal(e) ?? T('Kunne ikke opprette: {error}', { error: err.message }));
          return;
        }
        // no answer, a server failure, or a taken username: look for the person before saying anything
        const id = await confirmMade();
        if (id != null)
          return done(
            id,
            T('Ansatt opprettet. Svaret kom sent, men personen er funnet. Ingen invitasjon er sendt.'),
          );
        if (err?.code === 'username_taken') {
          toast(takenRefusal(e) ?? err.message);
          return;
        }
        setUnsure(
          T(
            'Vi fikk ikke svar fra Timefly og finner ingen ny ansatt med brukernavnet {u}. Ingenting ser ut til å være lagret. Skjemaet er beholdt, så du kan prøve igjen.',
            {
              u: (nw.username ?? '').trim(),
            },
          ),
        );
      }
    };
    const inp = (k: keyof UserWrite, label: string, type = 'text') => {
      const wrong = bad.includes(k);
      return (
        <Row label={label}>
          {type === 'date' ? (
            // the app's date field, not the browser's: its calendar button has the house focus ring (#272 B-V03)
            <span style={{ display: 'inline-flex', width: 240, maxWidth: '100%' }}>
              <DateInput
                // a birth date is in the past and opens decades back (#272 B-X02)
                {...(k === 'birth_date' ? birthDay() : {})}
                id={`ny-${k}`}
                className="inp"
                aria-label={label}
                aria-invalid={wrong || undefined}
                aria-describedby={wrong ? `ny-${k}-feil` : undefined}
                value={(nw[k] as string) ?? ''}
                onValueChange={(v) => {
                  setNw({ ...nw, [k]: v });
                  if (wrong) setBad(bad.filter((x) => x !== k));
                }}
              />
            </span>
          ) : (
            <input
              id={`ny-${k}`}
              className="inp"
              type={type}
              aria-label={label}
              aria-invalid={wrong || undefined}
              aria-describedby={wrong ? `ny-${k}-feil` : undefined}
              style={{ width: 240 }}
              value={(nw[k] as string) ?? ''}
              onChange={(e) => {
                setNw({ ...nw, [k]: e.target.value });
                if (wrong) setBad(bad.filter((x) => x !== k));
              }}
            />
          )}
          {wrong && (
            <small id={`ny-${k}-feil`} className="ferr" style={{ display: 'block', marginTop: 8 }}>
              {T('Må fylles ut')}
            </small>
          )}
        </Row>
      );
    };
    // back to the list it was opened from, with its team, status, task and search (#270 EMP-07)
    const from = (loc.state as { from?: string } | null)?.from ?? '/ansatte';
    const teamOptions = new Map<number, string>();
    for (const t of teamsQ.data ?? []) if (t.name) teamOptions.set(t.id, t.name);
    for (const x of rosterQ.data ?? [])
      for (const t of x.teams ?? []) {
        const tid = (t as { tenant_id?: number }).tenant_id;
        if (t.name && (tid == null || tid === tenant?.id)) teamOptions.set(t.id, t.name);
      }
    return (
      <div className="ans-prof">
        <div className="ans-top">
          <Link to={from} className="ans-back">
            <Icon name="chevronLeft" />
            {T('Ansatte')}
          </Link>
        </div>
        <PageHead title={T('Ny ansatt')} sub={T('Start med personen og avtalt arbeidstid.')} />
        {newDraft && typed && (
          <Notice
            tone="info"
            title={T('Ulagret utkast')}
            action={
              <Button
                ghost
                onClick={() => {
                  setNw(BLANK);
                  setWeek({ kind: null, hours: '' });
                  setTeam(teamFromScope);
                  dropDraft(tenant?.id, meId, 'ny');
                }}
              >
                {T('Forkast utkastet')}
              </Button>
            }
          >
            {T('Skjemaet er fylt ut fra et utkast som ikke er lagret. Ingen ansatt er opprettet ennå.')}
          </Notice>
        )}
        {unsure && (
          <div className="al warn" role="alert" data-testid="ny-ukjent">
            <Icon name="warn" />
            <span>{unsure}</span>
          </div>
        )}
        <Panel title={T('Person')} sub={T('Navn, fødselsdato og brukernavn må fylles ut.')}>
          {/* TF-REG03 (REG03-02): the whole form is locked while the create is on its way, so nothing typed
              then can be lost when the answer moves the page on; a refusal unlocks it with the draft kept */}
          <fieldset className="ans-newfs" disabled={w.create.isPending} aria-busy={w.create.isPending}>
            {inp('name', T('Fornavn'))}
            {inp('lastname', T('Etternavn'))}
            {inp('birth_date', T('Fødselsdato'), 'date')}
            {inp('username', T('Brukernavn'))}
            <Row label={T('E-post')}>
              <input
                className="inp"
                type="email"
                aria-label={T('E-post')}
                style={{ width: 240 }}
                placeholder={T('Kan stå tom')}
                value={nw.email ?? ''}
                onChange={(e) => setNw({ ...nw, email: e.target.value })}
              />
              <EmailTypo value={nw.email ?? ''} onFix={(email) => setNw({ ...nw, email })} />
            </Row>
            <Row label={T('Innlogging')}>
              <span className="hint" data-testid="no-email-note">
                {nw.email?.trim()
                  ? T(
                      'Opprettelse sender ingen invitasjon. Når personen skal logge inn, inviterer du fra Ansatte eller profilen.',
                    )
                  : T(
                      'Ingen e-post ennå. Personen kan planlegges. For å logge inn trenger personen en adresse, eller lenken fra «Vis lenke» på Ansatte.',
                    )}
              </span>
            </Row>
            <Row
              label={T('Team')}
              note={
                team
                  ? undefined
                  : T(
                      'Uten team: personen kan planlegges, men vises ikke under noe team i Vaktplan og Ansatte.',
                    )
              }
            >
              <select
                id="ny-team"
                className="inp"
                aria-label={T('Team')}
                style={{ width: 240 }}
                value={team}
                onChange={(e) => setTeam(e.target.value)}
                data-testid="ny-team"
              >
                <option value="">{T('Uten team')}</option>
                {[...teamOptions.entries()]
                  .sort((a, b) => a[1].localeCompare(b[1], 'nb'))
                  .map(([id, nm]) => (
                    <option key={id} value={String(id)}>
                      {nm}
                    </option>
                  ))}
              </select>
            </Row>
            {inp('phone', T('Telefon'))}
            <Row label={T('Lønnstype')}>
              <div className="seg">
                <button
                  type="button"
                  aria-pressed={nw.contract_type !== 'monthly'}
                  onClick={() => setNw({ ...nw, contract_type: 'hourly' })}
                >
                  {T('Timelønn')}
                </button>
                <button
                  type="button"
                  aria-pressed={nw.contract_type === 'monthly'}
                  onClick={() => setNw({ ...nw, contract_type: 'monthly' })}
                >
                  {T('Fastlønn')}
                </button>
              </div>
            </Row>
            {can('ansatte.loenn') &&
              (nw.contract_type === 'monthly'
                ? inp('monthly_rate', T('Månedslønn, kr'))
                : inp('hourly_rate', T('Timelønn, kr per time')))}
            <Row
              label={T('Avtalt arbeidstid')}
              note={T(
                'Heltid følger bedriftens uke (Innstillinger → Arbeidstid). Deltid: timer i en vanlig uke. Må velges.',
              )}
            >
              <div className="seg">
                <button
                  type="button"
                  aria-pressed={week.kind === 'heltid'}
                  onClick={() => setWeek({ ...week, kind: 'heltid' })}
                >
                  {T('Heltid (følger bedriften, {h} t)', { h: companyWeek })}
                </button>
                <button
                  type="button"
                  aria-pressed={week.kind === 'deltid'}
                  onClick={() => setWeek({ ...week, kind: 'deltid' })}
                >
                  {T('Deltid')}
                </button>
              </div>
              {week.kind === 'deltid' && (
                <input
                  className="inp"
                  inputMode="decimal"
                  aria-label={T('Deltid, timer per uke')}
                  style={{ width: 90, marginTop: 6 }}
                  placeholder="22,5"
                  value={week.hours}
                  onChange={(e) => setWeek({ ...week, hours: e.target.value })}
                />
              )}
            </Row>
            <Row
              label={T('Ansettelsesform')}
              note={T(
                'Fast, midlertidig eller tilkalling, som arbeidsavtalen sier (arbeidsmiljøloven § 14-9).',
              )}
            >
              <select
                className="inp"
                aria-label={T('Ansettelsesform')}
                style={{ width: 240 }}
                value={nw.employment_form ?? ''}
                onChange={(e) =>
                  setNw({
                    ...nw,
                    employment_form: (e.target.value || null) as 'fast' | 'midlertidig' | 'tilkalling' | null,
                  })
                }
              >
                <option value="">{T('Ikke oppgitt')}</option>
                <option value="fast">{T('Fast')}</option>
                <option value="midlertidig">{T('Midlertidig')}</option>
                <option value="tilkalling">{T('Tilkalling')}</option>
              </select>
            </Row>
            {nw.employment_form === 'midlertidig' && inp('end_date', T('Sluttdato'), 'date')}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <span className="sp" />
              <Button onClick={() => nav(from)}>{T('Avbryt')}</Button>
              {typed && (
                // keep-and-leave (#270): the draft waits in this tab; the next Ny ansatt opens with it
                <Button
                  onClick={() => {
                    setDirty('new-employee', null);
                    nav(from);
                  }}
                  data-testid="ny-behold"
                >
                  {T('Behold utkast')}
                </Button>
              )}
              <Button tone="pri" onClick={create} disabled={w.create.isPending}>
                <Icon name="plus" />
                {T('Opprett')}
              </Button>
            </div>
          </fieldset>
        </Panel>
      </div>
    );
  }

  if (userQ.isError)
    return <Empty>{T('Fant ikke personen: {error}', { error: (userQ.error as Error).message })}</Empty>;
  if (!u) return <Skeleton />;
  const name = fullName(u);
  // the job title: the list carries the company's role name as a fallback, the single read does not
  const jobTitle = (rosterQ.data ?? []).find((x) => x.id === u.id)?.title ?? u.title ?? null;
  const roster = (rosterQ.data ?? []) as Person[];
  const inQueue = !!queue && queue.ids.includes(u.id);
  const step = inQueue
    ? (() => {
        const ids = queue.ids;
        const i = ids.indexOf(u.id);
        const byId = new Map(roster.map((x) => [x.id, x]));
        return {
          prev: byId.get(ids[i - 1] ?? -1) ?? null,
          next: byId.get(ids[i + 1] ?? -1) ?? null,
          index: i + 1,
          total: ids.length,
        };
      })()
    : neighbours(roster, u.id);
  const created = (loc.state as { created?: boolean } | null)?.created === true;
  // the back link: the queue's list with its task, team, status and search, focus on this person; opened
  // from a Lønnsgrunnlag check (?tilbake=/lonn?...), back to that exact payroll address (#264)
  const payrollBack = (() => {
    const r = safeReturn(sp.get('tilbake'));
    return r && /^\/lonn(\?|$)/.test(r) ? r : null;
  })();
  const back = payrollBack ? (
    <Link to={payrollBack} className="ans-back" data-testid="ansatt-tilbake">
      <Icon name="chevronLeft" />
      {T('Tilbake til lønnsgrunnlaget')}
    </Link>
  ) : (
    <Link
      to={inQueue ? queue.origin : '/ansatte'}
      state={{ restore: u.id }}
      className="ans-back"
      data-testid="ansatt-tilbake"
    >
      <Icon name="chevronLeft" />
      {T(inQueue ? BACK_TO[queue.task] : 'Ansatte')}
    </Link>
  );
  const kept = loadDraft(tenant?.id, meId, u.id);
  const qTask = inQueue ? queue.task : 'all';
  const fresh = () => (qc.getQueryData(['users']) as Person[] | undefined) ?? roster;
  const nextUp =
    inQueue && (qTask === 'hours' || qTask === 'setup')
      ? nextInQueue(queue, u.id, roster, accessKnown)
      : null;
  const goNext = (n: Person) => {
    setSaved(null);
    const field = fieldFor(n, qTask);
    nav(`/ansatte/${n.id}${field ? `?rediger=${field}` : ''}`);
  };
  const mine = (shiftsQ.data?.shifts ?? []).filter((s) => s.user_id === u.id);
  const policy = breakPolicy(tenant);
  const abs = (absQ.data ?? [])
    .filter((a) => a.user_id === u.id)
    .sort((a, b) => b.start_date.localeCompare(a.start_date));
  const teams = (teamsQ.data ?? []).filter((t) => t.name);
  const seeLoenn = can('ansatte.loenn');
  const seeSens = can('ansatte.pnr');
  const mayEdit = can('ansatte.endre');
  // GET /admin/users/:id includes every membership row (admin/userController.js getUserById)
  const memberships = (u as ApiUserFull & { userTenantsList?: { tenant_id: number; role?: string }[] })
    .userTenantsList;
  const companies = (memberships ?? []).length;
  // the role in the active company (user_tenants), the same source Bedriftstilgang edits; users.role is never shown
  const roleHere = tenant ? roleIn(memberships, tenant.id) : null;
  // named from the company's own roles, so a company that calls its role Vaktleder reads as
  // Vaktleder here rather than as the nearest fixed word (custom roles, 2026-09-16)
  const roleHereText =
    roleHere == null
      ? T('Ingen tilgang til denne bedriften')
      : (rolesQ.data?.roles.find((r) => r.key === roleHere)?.name ?? roleHere);
  const setPassword = async () => {
    if (!pw) return;
    // the backend's only rule: a non-empty string (admin/userController.js updateUser `if (password)`)
    if (!pw.a) return setPw({ ...pw, msg: T('Skriv inn et passord') });
    if (pw.a !== pw.b) return setPw({ ...pw, msg: T('Passordene er ikke like') });
    setPw({ ...pw, busy: true, msg: null });
    try {
      await w.update.mutateAsync({ id: u.id, body: fullBody(u, { password: pw.a }) });
      toast(T('Nytt passord er satt'));
      setPw(null);
    } catch (e) {
      // an ApiError is the server's own refusal; anything else (network, a discarded answer) is no answer
      const refused = e instanceof ApiError && !(e instanceof StaleError);
      setPw({
        a: refused ? pw.a : '',
        b: refused ? pw.b : '',
        busy: false,
        unknown: !refused,
        msg: refused
          ? `${T('Kunne ikke sette passord')}: ${(e as Error).message}`
          : T('Resultatet er ukjent. Passordet kan være endret. Ingenting ble sendt på nytt.'),
      });
    }
  };

  // A manager reaches this page for the directory their scheduling and approvals need, and
  // the server sends them a name, a work contact, the teams in this company and nothing else.
  // Rendering the edit form with pieces hidden would show a status button and autosaving
  // fields the server refuses, and would read withheld values as missing ones: the role would
  // claim no access because the membership list is not in the answer, and every balance would
  // look unset. So this is its own view, with only what arrived (TF-SIMPLE-ROLES acceptance).
  if (!mayEdit) {
    const teamsHere = (u.teams ?? []).map((t) => t.name).filter(Boolean);
    return (
      <div className="ans-prof">
        <div className="ans-top">{back}</div>
        {/* a check elsewhere (Lønnsgrunnlag) sent a reader here to fix a field: say why nothing opens (#264) */}
        {askedField && (
          <Notice tone="info" title={T('Du har lesetilgang til denne ansatte')}>
            {T('Feltet kan bare rettes av noen som har tilgang til å endre ansatte. Ingenting er endret.')}
          </Notice>
        )}
        <PageHead
          title={name}
          sub={[jobTitle || T('Stilling ikke oppgitt'), teamsHere.join(', ') || T('Ingen team')].join(', ')}
          actions={
            u.active_status ? (
              <Status tone="ok">{T('Aktiv')}</Status>
            ) : (
              <Status tone="neu">{T('Inaktiv')}</Status>
            )
          }
        />
        <Panel title={T('Kontakt og team')} sub={T('Det du trenger for vaktplan og godkjenning')}>
          <Row label={T('E-post')}>
            <span>{u.email || T('Ingen e-post ennå')}</span>
          </Row>
          <Row label={T('Telefon')}>
            <span>{u.phone || '–'}</span>
          </Row>
          <Row label={T('Team')}>
            <span>{teamsHere.length ? teamsHere.join(', ') : T('Ingen team')}</span>
          </Row>
        </Panel>
        {/* #283: the planner and the Vaktplan read these; a manager sees them, an administrator sets them */}
        <FerdigheterPanel userId={u.id} mayEdit={false} />
        <p className="hint" style={{ maxWidth: '66ch' }} data-testid="directory-note">
          {T(
            'Personopplysninger, lønn, saldo og roller endres av en administrator. Du ser det du trenger for vaktplan, timeliste og fravær.',
          )}
        </p>
        {/* issue #70: whoever approves availability decides here without managing the person */}
        {can('tilgjengelighet.godkjenne') && tenant?.availability_requires_approval && (
          <Panel title={T('Tilgjengelighet')}>
            <TilgjengelighetTab userId={u.id} />
          </Panel>
        )}
      </div>
    );
  }

  const base = `/ansatte/${u.id}`;
  // account mail (owner, 2026-09-21): outward-facing, so the address is confirmed first
  const sendMail = (kind: 'welcome' | 'reset-link') => mail(u, kind);
  const tab = TABS.some((x) => x.key === fane) ? (fane as Tab) : '';
  const now = Date.now();
  const next = nextShift(mine, now);
  const duty = onDuty(mine, now);
  const wk = weekFigures(mine, monday, addDays(monday, 6), policy, u.max_hours_per_week);
  const teamNames = (u.teams ?? []).map((t) => t.name).filter(Boolean);
  const stepTo = (other: { id: number }) => nav(`/ansatte/${other.id}${tab ? `/${tab}` : ''}`);
  const gaps = setupItems(u, mayEdit);
  const statusText = !u.active_status ? (
    <span className="aduty off">{T('Inaktiv')}</span>
  ) : duty ? (
    <span className="aduty on">
      {T('På jobb nå, siden {t}', { t: oslo(duty.punch_in_time)?.time?.slice(0, 5) ?? '' })}
    </span>
  ) : (
    <span className="aduty">{T('Ikke på jobb nå')}</span>
  );
  return (
    <div className="ans-prof">
      <div className="ans-top">
        {back}
        <span className="sp" />
        {step.total > 1 && step.index > 0 && (
          <span className="stepper" data-testid="ansatt-stepper">
            <button
              type="button"
              className="stepbtn"
              disabled={!step.prev}
              onClick={() => step.prev && stepTo(step.prev)}
              aria-label={step.prev ? `${T('Forrige')}: ${fullName(step.prev)}` : T('Forrige')}
              title={step.prev ? fullName(step.prev) : undefined}
            >
              <Icon name="chevronLeft" />
            </button>
            <span className="stepn">
              {step.index} {T('av')} {step.total}
              {inQueue && qTask !== 'all' && `, ${T(TASK_NAME[qTask])}`}
            </span>
            <button
              type="button"
              className="stepbtn"
              disabled={!step.next}
              onClick={() => step.next && stepTo(step.next)}
              aria-label={step.next ? `${T('Neste')}: ${fullName(step.next)}` : T('Neste')}
              title={step.next ? fullName(step.next) : undefined}
            >
              <Icon name="chevronRight" />
            </button>
          </span>
        )}
      </div>
      <PageHead
        title={name}
        sub={
          <span className="sub">
            {jobTitle || T('Stilling ikke oppgitt')}
            {', '}
            {teamNames.length > 0 ? teamNames.join(', ') : T('Ingen team')}
            {', '}
            {statusText}
          </span>
        }
        actions={
          <>
            {u.active_status ? (
              <Status tone="ok">{T('Aktiv')}</Status>
            ) : (
              <Status tone="neu">{T('Inaktiv')}</Status>
            )}
            <Button onClick={() => edit()}>{T('Rediger')}</Button>
            <Menu
              tip={T('Konto og status')}
              trigger={
                <button type="button" className="btn" aria-label={T('Flere valg')}>
                  <Icon name="more" />
                </button>
              }
              items={[
                {
                  key: 'active',
                  label: u.active_status ? T('Sett inaktiv') : T('Sett aktiv'),
                  onSelect: () => {
                    // EMP-08: deactivating stops the login; the last working day is the Sluttdato, a separate fact
                    if (
                      u.active_status &&
                      !window.confirm(
                        T(
                          'Sette {name} inaktiv? Personen kan ikke logge inn og vises ikke blant de aktive. Vakter, timer og historikk beholdes. Siste arbeidsdag registreres som Sluttdato under Rediger, Arbeidstid og lønn.',
                          { name },
                        ),
                      )
                    )
                      return;
                    void patch(
                      { active_status: !u.active_status },
                      u.active_status ? T('Satt inaktiv') : T('Satt aktiv'),
                    );
                  },
                },
                ...(u.active_status && u.email
                  ? [
                      {
                        key: 'welcome',
                        label: T('Send velkomst-e-post'),
                        onSelect: () => sendMail('welcome'),
                      },
                      {
                        key: 'reset',
                        label: T('Send lenke for nytt passord'),
                        onSelect: () => sendMail('reset-link'),
                      },
                    ]
                  : []),
                // offered only for an account whose sole membership is this company (Codex resolution 2026-09-14); the server enforces the same rule
                ...(companies === 1
                  ? [
                      {
                        key: 'pw',
                        label: T('Sett nytt passord…'),
                        onSelect: () => setPw({ a: '', b: '', busy: false, msg: null, unknown: false }),
                      },
                    ]
                  : []),
              ]}
            />
          </>
        }
      >
        <Tabs
          label={T('Profil')}
          value={tab}
          items={TABS.map((x) => ({
            key: x.key,
            label: T(x.label),
            icon: x.icon,
            to: x.key ? `${base}/${x.key}` : base,
          }))}
        />
      </PageHead>
      {gaps.length > 0 && (
        <Notice tone="warn" title={T('Oppsett mangler')}>
          <span className="ans-gaps">
            {gaps.map((g) => (
              <button key={g.field} type="button" className="lnk" onClick={() => edit(g.field)}>
                {T(g.text)}
              </button>
            ))}
          </span>
        </Notice>
      )}
      {created && (
        <Notice
          tone="ok"
          title={T('Ansatt opprettet. Ingen invitasjon er sendt.')}
          action={
            accessKnown &&
            u.active_status && (
              <Button onClick={() => setInviting(true)} data-testid="ansatt-inviter">
                {T('Se og inviter')}
              </Button>
            )
          }
        >
          {T(
            'Felt du ikke fylte ut står som manglende til de er registrert. Personen kan ikke logge inn før du inviterer.',
          )}
        </Notice>
      )}
      {saved?.id === u.id && (
        <Notice
          tone="ok"
          title={T('Opplysningene er lagret.')}
          action={
            saved.next ? (
              <Button onClick={() => saved.next && goNext(saved.next)} data-testid="ansatt-neste">
                {T('Neste ansatt')} <Icon name="arrowRight" />
              </Button>
            ) : (
              inQueue && (
                <Link className="btn" to={queue.origin} state={{ restore: u.id }}>
                  {T(BACK_TO[qTask])}
                </Link>
              )
            )
          }
        >
          {T(
            '{n, plural, =0 {Ingen gjenstår i denne oppgaven.} one {# gjenstår i denne oppgaven.} other {# gjenstår i denne oppgaven.}}',
            {
              n: saved.left,
            },
          )}
        </Notice>
      )}
      {kept && !editing && (
        <Notice
          tone="info"
          title={T('Ulagret utkast')}
          action={
            <>
              <Button onClick={() => edit()} data-testid="ansatt-utkast-fortsett">
                {T('Fortsett redigeringen')}
              </Button>
              <Button
                ghost
                onClick={() => {
                  dropDraft(tenant?.id, meId, u.id);
                  redraw((n) => n + 1);
                }}
              >
                {T('Forkast utkastet')}
              </Button>
            </>
          }
        >
          {T('Endringer i {name} er ikke lagret. De vises når du åpner redigeringen.', { name })}
        </Notice>
      )}
      {accessKnown && (
        <InviteReview
          ids={inviting ? [u.id] : null}
          people={roster}
          meId={meId}
          onClose={() => setInviting(false)}
          onRetry={() => setInviting(true)}
        />
      )}
      <Drawer
        open={!!pw}
        onClose={() => setPw(null)}
        blockClose={() => !!pw?.busy}
        title={T('Sett nytt passord')}
        sub={name}
        footer={
          <>
            <span className="sp" />
            <Button onClick={() => setPw(null)} disabled={pw?.busy}>
              {pw?.unknown ? T('Lukk') : T('Avbryt')}
            </Button>
            {!pw?.unknown && (
              <Button tone="pri" onClick={setPassword} disabled={pw?.busy}>
                {pw?.busy ? T('Lagrer…') : T('Lagre')}
              </Button>
            )}
          </>
        }
      >
        {pw && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!pw.unknown) setPassword();
            }}
          >
            <Row label={T('Nytt passord')}>
              <input
                className="inp"
                type="password"
                autoComplete="new-password"
                aria-label={T('Nytt passord')}
                style={{ width: 240 }}
                value={pw.a}
                disabled={pw.busy || pw.unknown}
                onChange={(e) => setPw({ ...pw, a: e.target.value, msg: null })}
              />
            </Row>
            <Row label={T('Bekreft passord')}>
              <input
                className="inp"
                type="password"
                autoComplete="new-password"
                aria-label={T('Bekreft passord')}
                style={{ width: 240 }}
                value={pw.b}
                disabled={pw.busy || pw.unknown}
                onChange={(e) => setPw({ ...pw, b: e.target.value, msg: null })}
              />
            </Row>
            <p className="hint" style={{ maxWidth: '60ch' }}>
              {T(
                'Det gamle passordet vises ikke og hentes ikke. Den ansatte logger inn med det nye i appen og på nett.',
              )}
            </p>
            {pw.msg && (
              <div className={`al ${pw.unknown ? 'warn' : 'dgr'}`} role="alert">
                <span>{pw.unknown ? '?' : <Icon name="stop" />}</span>
                <span>{pw.msg}</span>
              </div>
            )}
          </form>
        )}
      </Drawer>
      {editing && (
        <EditDrawer
          key={u.id}
          open
          startTab={editing.tab}
          focus={editing.focus}
          u={u}
          reload={async () => {
            await qc.invalidateQueries({ queryKey: ['user', u.id] });
            return (qc.getQueryData(['user', u.id]) as typeof u | undefined) ?? u;
          }}
          onNext={
            nextUp
              ? () => {
                  const n = nextInQueue(queue as NonNullable<typeof queue>, u.id, fresh(), accessKnown);
                  if (n) goNext(n);
                }
              : null
          }
          base={base}
          teams={teams}
          seeLoenn={seeLoenn}
          seeSens={seeSens}
          roleText={roleHereText}
          onClose={() => setEditing(false)}
          save={async (body, base) => {
            try {
              const saved = await w.update.mutateAsync({ id: u.id, body: fullBody(base, body) });
              // inside a task the «Opplysningene er lagret» notice says it; one confirmation, not two
              if (!(queue?.ids.includes(u.id) && qTask !== 'all')) toast(T('Lagret'));
              // a changed start date in a year with holiday charged leaves the balance as it was (ADR 0024)
              const check = (saved as { holiday_check?: number[] } | undefined)?.holiday_check;
              if (check?.length)
                toast(
                  T('Ansatt fra er endret, men det er trukket ferie i {year}. Sjekk feriesaldoen for hånd.', {
                    year: check.join(' og '),
                  }),
                );
            } catch (e) {
              // a conflict goes back as it came, so the drawer can show the record as it is now (#270)
              if (e instanceof ApiError && e.code === 'revision_conflict') throw e;
              // the field the server named goes with it, so the drawer opens that field (#294 ONB-07)
              const field =
                e instanceof ApiError ? (e.body as { field?: unknown } | undefined)?.field : undefined;
              throw Object.assign(
                new Error(takenRefusal(e) ?? `${T('Kunne ikke lagre')}: ${(e as Error).message}`),
                typeof field === 'string' ? { field } : {},
              );
            }
            // the save's own answer moved the list (useUserWrites refetches it before resolving): count
            // what is left in the task and who comes next by id, never by the row's old position
            if (queue?.ids.includes(u.id) && qTask !== 'all') {
              const list = fresh();
              setSaved({
                id: u.id,
                left: remaining(queue, list, accessKnown),
                next: nextInQueue(queue, u.id, list, accessKnown),
              });
            }
          }}
        />
      )}
      <TabPanel tab={tab}>
        {tab === '' && (
          <Figures base={base} next={next} week={wk} u={u} abs={abs} shiftsQ={shiftsQ} absQ={absQ} />
        )}
        {tab === '' && (
          <Oversikt
            base={base}
            u={u}
            next={next}
            mine={mine}
            abs={abs}
            policy={policy}
            breakMode={modeOf(tenant)}
            roleText={roleHereText}
            jobTitle={jobTitle}
            login={accessKnown ? (roster.find((x) => x.id === u.id) ?? null) : null}
            seeLoenn={seeLoenn}
            seeSens={seeSens}
            onEdit={edit}
            shiftsQ={shiftsQ}
            absQ={absQ}
            files={
              <Panel
                item
                title={
                  <span className="ovt">
                    <span className="ovi">
                      <Icon name="document" />
                    </span>
                    {T('Dokumenter')}
                  </span>
                }
              >
                <p className="ovnone">
                  {T(
                    'Personlige dokumenter gjelder bare denne ansatte. Felles dokumenter deles av alle i bedriften.',
                  )}
                </p>
                <Link className="ovmore" to={`${base}/dokumenter`}>
                  {T('Åpne dokumenter')} <Icon name="chevronRight" />
                </Link>
              </Panel>
            }
          />
        )}
        {tab === 'vakter' && <VakterTab u={u} />}
        {tab === 'tid' && (
          <TidTab
            u={u}
            mine={mine}
            monday={monday}
            policy={policy}
            breakMode={modeOf(tenant)}
            seeLoenn={seeLoenn}
            onEdit={(f) => (f ? edit(f) : setEditing({ tab: 'arbeid' }))}
          />
        )}
        {tab === 'fravaer' && <FravaerTab u={u} abs={abs} shifts={mine} absQ={absQ} />}
        {tab === 'tilgjengelighet' && <TilgjengelighetTab userId={u.id} />}
        {tab === 'dokumenter' && <Files userId={u.id} />}
        {tab === 'tilgang' && (
          <TilgangTab
            u={u}
            roleKey={roleHere}
            roleText={roleHereText}
            body={(c) => ({ ...fullBody(u, {}), ...c })}
            onEditTeams={() => setEditing({ tab: 'team' })}
          />
        )}
      </TabPanel>
    </div>
  );
}
